import React, { useEffect, useRef } from "react";
import p5 from "p5";
import { useSelector, useDispatch } from "react-redux";
import { generateCladeImages, generateDefaultImages, processAllImages, generateGDMImages } from "../helpers/preprocessImages";
import {
  genomeOptions,
  geneHeight,
  backgroundColour,
  baseline,
  componentWidth,
  backgroundTextColour,
} from "../constants";
import { changeChromosomeSelection } from "../redux/reducers/chromosomeSlice";
import { changePointerPosition, changePointerSelection } from "../redux/reducers/pointerSlice";
import formatActionPayload from "../redux/formatActionPayload";

import { updateChromosomeSelection } from "../redux/actions";

const Genome = (props) => {

  const containerRef = useRef();
  const dispatch = useDispatch();
  const selectedChromosomes = useSelector(state => state.chromosomes);
  const chromosome = selectedChromosomes.current[props.genomeData.genome];
  const selectedSecondaryTracks = useSelector(state => state.dashboard.secondaryTrack);

  //console.log(props.genomeData)

  const Sketch = (p) => {
    let chromosomes, componentHeight, differenceSum, cw;
    let images = {};
    let secondaryTrack = new Map();
    let proportions = {};
    let boundaries = {};

    p.preload = () => {
      for (let k in props.preprocessedImages) {
        images[k] = p.loadImage(props.preprocessedImages[k]);
      }

      for (const [key, val] of props.preprocessedGDM) {
        secondaryTrack.set(key, p.loadImage(val))
        //gdmImages[k] = p.loadImage(props.preprocessedGDM[k]);
      }
    }

    p.setup = () => {
      // Since format is the same, should sort based on chromosome #
      chromosomes = Object.keys(props.genomeData.repeatData).sort();
      cw = componentWidth - (5 * (chromosomes.length));

      // Find the entire positional size of the genome
      differenceSum = Object.values(props.genomeData.chromosomeBoundaries).reduce((acc, b) => (
        acc + (b.max - b.min)
      ), 0);
      // calculate each chromosome's size in the genome
      proportions = createProportions(differenceSum);
      boundaries = createBoundaries(proportions);

      // // set height of component based on user's selections
      // if (props.showGDM) componentHeight = geneHeight*(props.clade.length)*(props.completeFilter.length) + (2*baseline);
      // else componentHeight = geneHeight*(props.clade.length)*(props.completeFilter.length) + baseline

      componentHeight = (props.clade.length * props.completeFilter.length + selectedSecondaryTracks.length + 1) * geneHeight + baseline;
      p.createCanvas(componentWidth, componentHeight+3);
      p.noLoop();
    };

    p.draw = () => {
      p.background(backgroundColour);

      // For imaging processing only uncomment 1 at a time (won't work o/w)
      //generateDefaultImages(p, chromosomes, "fragment", props.genomeData)
      //generateGDMImages(p, chromosomes, props.genomeData, selectedSecondaryTracks);

      //generateCladeImages(p, chromosomes, "fragment", props.genomeData);
      //generateDefaultImages(p, chromosomes, "all", props.genomeData);

      for (let i=0; i<chromosomes.length; i++) {
        let count = 0;
        for (let j=0; j<props.clade.length; j++) {
          for (let k=0; k<props.completeFilter.length; k++) {
            // based on current clade and chromsome, decide what image is needed
            let assetName;
            if (props.completeFilter[k] === "all") assetName = `${props.genomeData.genome}_${props.clade[j]}_${i+1}`;
            else assetName = `${props.genomeData.genome}_${props.clade[j]}_${props.completeFilter[k]}_${i+1}`;

            // based on the chromosome size in genome, resize the image
            let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
            p.image(images[assetName], boundaries[id].start,  geneHeight*count +baseline, proportions[id]*cw, geneHeight);
            count += 1;
          }
          // // based on current clade and chromsome, decide what image is needed
          // let assetName;
          // if (props.completeFilter === "all") assetName = `${props.genomeData.genome}_${props.clade[i]}_${j+1}`;
          // else assetName = `${props.genomeData.genome}_${props.clade[i]}_${props.completeFilter}_${j+1}`;

          // // based on the chromosome size in genome, resize the image
          // let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${j+1}`;
          // p.image(images[assetName], boundaries[id].start,  geneHeight*(i)+baseline, proportions[id]*cw, geneHeight);
        }
      }



      for (let i=0; i<chromosomes.length; i++) {
        let count = 1;
        for (let j=0; j<selectedSecondaryTracks.length; j++) {
          let assetName = `${props.genomeData.genome}_${selectedSecondaryTracks[j]}_${i+1}`;
          let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
          
          p.image(secondaryTrack.get(assetName), boundaries[id].start, (props.clade.length*props.completeFilter.length)*geneHeight + geneHeight + geneHeight*count, proportions[id]*cw, geneHeight);
         
          count += 1;
        }
        // identify the GDM we need
        //let assetName = `${props.genomeData.genome}_GDM_${i+1}`;

        // resize according to the size of the GDM's corresponding chromosome
        //let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
        //p.image(gdmImages[assetName], boundaries[id].start, geneHeight*props.clade.length*props.completeFilter.length + baseline, proportions[id]*cw, geneHeight);
      }

      //p.text("test", 0, (props.clade.length*props.completeFilter.length)*geneHeight + geneHeight + geneHeight + 10)
  

      for (let i = 0; i < chromosomes.length; i++) {
        let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
        // if the chromosome is selected, draw a white outline around it
        if (chromosome.charAt(chromosome.length-1) == (i+1)) {
          p.strokeWeight(2.5);
          p.stroke(255);
        }
        else {
          p.stroke(backgroundTextColour);
          p.strokeWeight(0.5);
        } 

        // draw bounding boxes
        p.noFill();
        p.rect(boundaries[id].start, baseline, proportions[id]*cw, componentHeight-baseline);
        //add chromosome labels
        p.strokeWeight(0.5);
        p.textSize(12);
        p.fill(backgroundTextColour);
        p.text(`Chr${i+1}`, boundaries[id].start, 15);
      }
    };

    p.mouseClicked = () => {
      // Only check x coord if y coord is on the chromo map
      if (typeof chromosomes !== "undefined") {
        if (p.mouseY >= 0 && p.mouseY <= componentHeight) {
          for (let i = 0; i <= chromosomes.length; i++) {
            let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
            if (typeof boundaries[id] !== 'undefined') {
              // if click is on a chromosome, select it and pass up to the parent
              if (p.mouseX >= boundaries[id].start && p.mouseX <= boundaries[id].end) {
                if (i <= chromosomes.length - 1) {
                  let selectedChromosome = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i + 1}`;
                  let newChromosomeSelection = {...selectedChromosomes.current, [props.genomeData.genome]: selectedChromosome};
                  dispatch(updateChromosomeSelection(props.genomeData.genome, selectedChromosome));
                  // dispatch(changeChromosomeSelection(formatActionPayload(props.genomeData.genome, selectedChromosome)));
                  // dispatch(changePointerPosition(formatActionPayload(props.genomeData.genome, -10)));
                  // dispatch(changePointerSelection(formatActionPayload(props.genomeData.genome, [])));
                }
              }
            }
          }
        }
      }
    };

    const createProportions = (differenceSum) => {
      let proportions = {};
      for (let i=0; i<props.clade.length; i++) {
        for (let j=0; j<chromosomes.length; j++) {
          // based on the chromosome size in genome, calculate the relative size of the image
          let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${j+1}`;
          let boundary = props.genomeData.chromosomeBoundaries[id];
          proportions[id] = (boundary.max - boundary.min)/differenceSum;
        }
      }
      return proportions;
    }

    const createBoundaries = (proportions) => {
      let newPos = 0;
      let boundaries = {};
      for (let i=0; i<chromosomes.length; i++) {
        let id = `${genomeOptions[props.genomeData.genome.toUpperCase()].chromosomeFormat}${i+1}`;
        boundaries[id] = {"start": newPos, "end": newPos + (proportions[id]*cw)}
        newPos = newPos + (proportions[id]*cw) + 5;
      }
      return boundaries;
    }
  };

  useEffect(() => {
    let inst = new p5(Sketch, containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => inst.remove();
  }, [props.clade, chromosome, props.completeFilter, selectedSecondaryTracks]);
  
  return (
    <div ref={containerRef}> </div>
  )
};
export default Genome;